import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Checkbox } from "@libs/zapi-ui/ds/components/checkbox";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@libs/zapi-ui/ds/components/dialog";
import { Field, FieldDescription, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/ds/components/select";
import { MODEL_CHOOSER_OPTIONS, TModelType } from "@/zapi-frontend/shared/types/models";
import { AnalysisDropdown } from "@/zapi-frontend/widgets/CreateProjectForm/ui/AnalysisDropdown";
import { ProjectErrors, TProjectDataWithoutRepository } from "@/zapi-frontend/widgets/CreateProjectForm/model/types";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";

type TAnalysisSettingsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    projectData: TProjectDataWithoutRepository;
    selectedModel: TModelType;
    skipPrefill: boolean;
  }) => void;
  projectData: TProjectDataWithoutRepository;
  errors: ProjectErrors;
  setErrors: (errors: ProjectErrors) => void;
  selectedModel: TModelType;
  skipPrefill: boolean;
};

export function AnalysisSettingsModal({
  isOpen,
  onClose,
  onSave,
  projectData,
  errors,
  setErrors,
  selectedModel,
  skipPrefill
}: TAnalysisSettingsModalProps) {
  const { t } = useTranslation("createProject");
  const [draftProjectData, setDraftProjectData] = useState<TProjectDataWithoutRepository>(projectData);
  const [draftModel, setDraftModel] = useState<TModelType>(selectedModel);
  const [draftSkipPrefill, setDraftSkipPrefill] = useState(skipPrefill);
  const modelId = useId();
  const skipId = useId();
  // Claude-code-generation projects have no template, so the analysis type picker
  // (and its per-chapter instructions) is a legacy-only control.
  const claudeGeneration = useFeatureFlag("claude-code-generation");

  useEffect(() => {
    if (!isOpen) return;
    setDraftProjectData(projectData);
    setDraftModel(selectedModel);
    setDraftSkipPrefill(skipPrefill);
  }, [isOpen, projectData, selectedModel, skipPrefill]);

  const updateErrors = (updates: Partial<ProjectErrors>) => {
    setErrors({ ...errors, ...updates });
  };

  const handleSave = () => {
    onSave({
      projectData: draftProjectData,
      selectedModel: draftModel,
      skipPrefill: draftSkipPrefill
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        initialFocus={false}
        closeLabel={t("newProject.close")}
        className="gap-5 rounded-2xl sm:max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader>
          <DialogTitle className="typo-h5">{t("newProject.analysisSettings")}</DialogTitle>
        </DialogHeader>
        {claudeGeneration === false && (
          <AnalysisDropdown
            appearance="design-system"
            value={draftProjectData.projectTemplateSlug}
            onChange={(slug) => {
              setDraftProjectData({ ...draftProjectData, projectTemplateSlug: slug });
              updateErrors({ projectTemplateSlug: "" });
            }}
            error={errors.projectTemplateSlug}
            projectInstructions={draftProjectData.projectInstructions || ""}
            chaptersInstructions={draftProjectData.chaptersInstructions || {}}
            presentationPrompt={draftProjectData.presentationPrompt || ""}
            onInstructionsChange={(projectInstructions, chaptersInstructions) => {
              setDraftProjectData({ ...draftProjectData, projectInstructions, chaptersInstructions });
            }}
            onPresentationPromptChange={(value) => {
              setDraftProjectData({ ...draftProjectData, presentationPrompt: value });
            }}
          />
        )}
        <Field>
          <FieldLabel htmlFor={modelId}>{t("newProject.generationModel")}</FieldLabel>
          <Select
            items={MODEL_CHOOSER_OPTIONS}
            value={draftModel}
            onValueChange={(next) => typeof next === "string" && setDraftModel(next as TModelType)}
          >
            <SelectTrigger id={modelId} className="w-full" data-testid="analysis-settings-model-select-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MODEL_CHOOSER_OPTIONS.map((model) => (
                <SelectItem key={model.value} value={model.value}>
                  {model.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field orientation="horizontal" className="items-start">
          <Checkbox
            id={skipId}
            checked={draftSkipPrefill}
            onCheckedChange={(checked) => setDraftSkipPrefill(checked === true)}
            data-testid="analysis-settings-skip-prefill-checkbox"
          />
          <div className="flex flex-col gap-1">
            <FieldLabel htmlFor={skipId}>
              {claudeGeneration ? t("newProject.skipAnalysis") : t("newProject.skipChapters")}
            </FieldLabel>
            <FieldDescription>
              {claudeGeneration ? t("newProject.skipAnalysisNote") : t("newProject.skipChaptersNote")}
            </FieldDescription>
          </div>
        </Field>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" shape="pill" data-testid="analysis-settings-cancel-button" />}>
            {t("newProject.cancel")}
          </DialogClose>
          <Button shape="pill" onClick={handleSave} data-testid="analysis-settings-save-button">
            {t("newProject.saveChanges")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
